import { useState, createRef } from 'react'
import StrapiZoneMapper from '../components/StrapiDynamicZones/StrapiZoneMapper'
import {
  PowDynamicFormZoneInputType
} from '../components/StrapiDynamicZones/Zones/PowDynamicFormZone'
import {
  Button,
  Variant as ButtonVariant,
  Size as ButtonSize,
  Icon as ButtonIcon,
} from './Button'
import { UseFormRegister, useForm, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Captcha } from './forms/Captcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'

export type PowDynamicFormData = {
  data: FormProps
}

export type FormProps = {
  attributes : {
    title: string,
    description: string,
    inputFields: PowDynamicFormZoneInputType[],
    hCaptcha: boolean,
    submitButtonText: string,
    formId: string,
  }
}

export function PowDynamicForm({ data }: PowDynamicFormData) {
  const { title, description, inputFields, submitButtonText } = data?.attributes

  const { t: translate } = useTranslation()

  const [pauseSubmit, setPauseSubmit] = useState(false)
  const [errorSubmitting, setErrorSubmitting] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm()

  if (data.attributes.hCaptcha) {
    register('captchaToken', {
      required: 'Please complete the captcha'
    })
  }

  const captchaRef = createRef<HCaptcha>()

  async function submitForm(formData: any) {
    setPauseSubmit(true)
    const res = await fetch('https://powcontactus.azurewebsites.net/api/DynamicForm', {
      method: 'POST',
      body: JSON.stringify({...formData, formId: data.attributes.formId}),
    })
    if (res.status !== 200) {
      setErrorSubmitting(true)
      throw new Error('Error submitting')
    } else {
      setSubmitted(true)
      reset()
    }
    setPauseSubmit(false)
  }

  return (
    <div className="col-span-12 flex justify-center">
      <div className="bg-white flex flex-wrap gap-8 justify-center max-w-916px mb-5 p-10 shadow-lg md:flex-nowrap">
        <div className="space-y-8 w-full md:w-1/2">
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <form
          onSubmit={handleSubmit((formData) => {
            if (data.attributes.hCaptcha) captchaRef?.current?.execute()
            submitForm(formData).then(() => {
              setSubmitted(true)
              captchaRef?.current?.resetCaptcha()
            }).catch((e) => console.error(e))
          })}
          className="w-full md:w-1/2 grid grid-cols-2 gap-8"
        >
          {inputFields?.map((zone, i) => (
              <StrapiZoneMapper<{ register: UseFormRegister<any>, errors: FieldErrors<any>  }>
                key={zone.__typename + i}
                dynamicZoneName={zone.__typename}
                register={register}
                {...zone}
                errors={errors}
              />
          ))}
          {data.attributes.hCaptcha && (
            <div className="col-span-2">
              <Captcha
                onVerify={(token: string) => {
                  setValue('captchaToken', token, {
                    shouldValidate: true,
                  })
                }}
                errorMessage={errors?.captchaToken?.message}
              />
            </div>
          )}
          <div className="col-span-2">
            <Button
              variant={ButtonVariant.PRIMARY}
              icon={ButtonIcon.NONE}
              disabled={isSubmitted || pauseSubmit}
              type="submit"
              label={submitButtonText}
            />
          </div>
          <div className="col-span-2">
            {!!isSubmitted && (
              <span>{translate('thanksForNewsletterSignUp')}</span>
            )}
            {!!errorSubmitting && (
              <span>{translate('errorSubmittingMessage')}</span>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
